import { createNode } from './createNode'
import * as THREE from '../extras/three'
import CustomShaderMaterial from '../libs/three-custom-shader-material'

const groupTypes = ['Scene', 'Group', 'Object3D']

export function glbToNodes(glb, world) {
  function registerNode(name, data) {
    const node = createNode(name, data)
    return node
  }
  function parse(object3ds, parentNode) {
    for (const object3d of object3ds) {
      const props = object3d.userData || {}
      const isSkinnedMeshRoot = !!object3d.children.find(c => c.isSkinnedMesh)
      // SkinnedMesh (root)
      if (isSkinnedMeshRoot) {
        const node = registerNode('skinnedmesh', {
          id: object3d.name,
          object3d,
          animations: glb.animations,
          castShadow: props.castShadow,
          receiveShadow: props.receiveShadow,
          active: props.active,
          position: object3d.position.toArray(),
          quaternion: object3d.quaternion.toArray(),
          scale: object3d.scale.toArray(),
        })
        if (parentNode.name === 'lod' && props.maxDistance) {
          parentNode.insert(node, props.maxDistance)
        } else {
          parentNode.add(node)
        }
        // parse(object3d.children, node)
      }
      // Snap (custom node)
      else if (props.node === 'snap') {
        const node = registerNode('snap', {
          id: object3d.name,
          position: object3d.position.toArray(),
          quaternion: object3d.quaternion.toArray(),
          scale: object3d.scale.toArray(),
        })
        parentNode.add(node)
        parse(object3d.children, node)
      }
      // LOD (custom node)
      else if (props.node === 'lod') {
        const node = registerNode('lod', {
          id: object3d.name,
          position: object3d.position.toArray(),
          quaternion: object3d.quaternion.toArray(),
          scale: object3d.scale.toArray(),
          scaleAware: props.scaleAware,
        })
        parentNode.add(node)
        parse(object3d.children, node)
      }
      // RigidBody (custom node)
      else if (props.node === 'rigidbody') {
        const node = registerNode('rigidbody', {
          id: object3d.name,
          type: props.type,
          mass: props.mass,
          position: object3d.position.toArray(),
          quaternion: object3d.quaternion.toArray(),
          scale: object3d.scale.toArray(),
        })
        parentNode.add(node)
        parse(object3d.children, node)
      }
      // Collider (custom node)
      else if (props.node === 'collider' && object3d.isMesh) {
        // NOTE: in blender if you export a single object with node:collider but it has multiple materials, it converts this into a Group with one Mesh for each material.
        // but since the Group is the one that has the collider custom property, it won't work as expected. we could hack to fix this, but i think it adds a layer of indirection.
        // colliders should not have materials on them.
        // console.error('TODO: glbToNodes collider for box/sphere in blender?')
        const node = registerNode('collider', {
          id: object3d.name,
          type: 'geometry',
          geometry: object3d.geometry,
          convex: props.convex,
          trigger: props.trigger,
          layer: props.layer,
          position: object3d.position.toArray(),
          quaternion: object3d.quaternion.toArray(),
          scale: object3d.scale.toArray(),
        })
        parentNode.add(node)
        parse(object3d.children, node)
      }
      // Mesh
      else if (object3d.type === 'Mesh') {
        // experimental splatmaps
        if (props.exp_terrain && !world.network.isServer) {
          setupTerrain(object3d, world)
        } else if (props.exp_splatmap && !world.network.isServer) {
          setupSplatmap(object3d)
        }
        // wind effect
        else if (object3d.material.userData.wind) {
          addWind(object3d, world)
        }
        const hasMorphTargets = object3d.morphTargetDictionary || object3d.morphTargetInfluences?.length > 0
        const node = registerNode('mesh', {
          id: object3d.name,
          type: 'geometry',
          geometry: object3d.geometry,
          material: object3d.material,
          linked: !hasMorphTargets && !object3d.material.transparent,
          castShadow: props.castShadow,
          receiveShadow: props.receiveShadow,
          visible: props.visible, // DEPRECATED: use Node.active
          active: props.active,
          position: object3d.position.toArray(),
          quaternion: object3d.quaternion.toArray(),
          scale: object3d.scale.toArray(),
        })
        if (parentNode.name === 'lod' && props.maxDistance) {
          parentNode.insert(node, props.maxDistance)
        } else {
          parentNode.add(node)
        }
        parse(object3d.children, node)
      }
      // SkinnedMesh
      else if (object3d.type === 'SkinnedMesh') {
        // ...
      }
      // Object3D / Group / Scene
      else if (groupTypes.includes(object3d.type)) {
        const node = registerNode('group', {
          id: object3d.name,
          position: object3d.position.toArray(),
          quaternion: object3d.quaternion.toArray(),
          scale: object3d.scale.toArray(),
        })
        parentNode.add(node)
        parse(object3d.children, node)
      }
    }
  }
  const root = registerNode('group', {
    id: '$root',
  })
  parse(glb.scene.children, root)
  // console.log('$root', root)
  return root
}

function addWind(mesh, world) {
  if (!world.wind) return
  const uniforms = world.wind.uniforms
  if (mesh.material.hasWind) return
  mesh.material.hasWind = true
  // console.log('added wind to', mesh.name)
  mesh.material.onBeforeCompile = shader => {
    if (!shader.defines) shader.defines = {}
    shader.defines.USE_WIND = 1
    shader.uniforms.time = uniforms.time
    shader.uniforms.strength = uniforms.strength
    shader.uniforms.direction = uniforms.direction
    shader.uniforms.speed = uniforms.speed
    shader.uniforms.noiseScale = uniforms.noiseScale
    shader.uniforms.ampScale = uniforms.ampScale
    shader.uniforms.freqMultiplier = uniforms.freqMultiplier

    const height = mesh.geometry.boundingBox.max.y * mesh.scale.y

    shader.uniforms.height = { value: height } // prettier-ignore
    shader.uniforms.stiffness = { value: 0 }

    // BUG: somehow the wind shader code below is added to other meshes
    // so we wrap it in an ifdef. this might be a bug with CSM because disabling
    // the prepareMaterial in Stage.js the issues goes away
    // tbh the wind code should probably be part
    // of the global shader anyway, same with things like outlines etc.

    shader.vertexShader = shader.vertexShader.replace(
      '#include <common>',
      `
      uniform float time;
      uniform float strength;
      uniform vec3 direction;
      uniform float speed;
      uniform float noiseScale;
      uniform float ampScale;
      uniform float freqMultiplier;

      uniform float height;
      uniform float stiffness;

      ${snoise}

      #include <common>
      `
    )

    shader.vertexShader = shader.vertexShader.replace(
      '#include <begin_vertex>',
      `
      #include <begin_vertex>

      #ifdef USE_WIND
        vec4 worldPos = vec4(position, 1.0);
        #ifdef USE_INSTANCING
          worldPos = instanceMatrix * worldPos;
        #endif
        worldPos = modelMatrix * worldPos;

        float heightFactor = position.y / height;
        float noiseFactor = snoise(worldPos.xyz * noiseScale + time * speed);
        vec3 displacement = sin(time * freqMultiplier + worldPos.xyz) * noiseFactor * ampScale * heightFactor * (1.0 - stiffness);
        transformed += strength * displacement * direction;
      #endif
      `
    )
  }
}

const snoise = `
  //	Simplex 3D Noise 
  //	by Ian McEwan, Stefan Gustavson (https://github.com/stegu/webgl-noise)
  //
  vec4 permute(vec4 x){
    return mod(((x*34.0)+1.0)*x, 289.0);
  }
  vec4 taylorInvSqrt(vec4 r){ 
    return 1.79284291400159 - 0.85373472095314 * r; 
  }

  float snoise(vec3 v){ 
  const vec2  C = vec2(1.0/6.0, 1.0/3.0) ;
  const vec4  D = vec4(0.0, 0.5, 1.0, 2.0);

  // First corner
  vec3 i  = floor(v + dot(v, C.yyy) );
  vec3 x0 =   v - i + dot(i, C.xxx) ;

  // Other corners
  vec3 g = step(x0.yzx, x0.xyz);
  vec3 l = 1.0 - g;
  vec3 i1 = min( g.xyz, l.zxy );
  vec3 i2 = max( g.xyz, l.zxy );

  //  x0 = x0 - 0. + 0.0 * C 
  vec3 x1 = x0 - i1 + 1.0 * C.xxx;
  vec3 x2 = x0 - i2 + 2.0 * C.xxx;
  vec3 x3 = x0 - 1. + 3.0 * C.xxx;

  // Permutations
  i = mod(i, 289.0 ); 
  vec4 p = permute( permute( permute( 
      i.z + vec4(0.0, i1.z, i2.z, 1.0 ))
    + i.y + vec4(0.0, i1.y, i2.y, 1.0 )) 
    + i.x + vec4(0.0, i1.x, i2.x, 1.0 ));

  // Gradients
  // ( N*N points uniformly over a square, mapped onto an octahedron.)
  float n_ = 1.0/7.0; // N=7
  vec3  ns = n_ * D.wyz - D.xzx;

  vec4 j = p - 49.0 * floor(p * ns.z *ns.z);  //  mod(p,N*N)

  vec4 x_ = floor(j * ns.z);
  vec4 y_ = floor(j - 7.0 * x_ );    // mod(j,N)

  vec4 x = x_ *ns.x + ns.yyyy;
  vec4 y = y_ *ns.x + ns.yyyy;
  vec4 h = 1.0 - abs(x) - abs(y);

  vec4 b0 = vec4( x.xy, y.xy );
  vec4 b1 = vec4( x.zw, y.zw );

  vec4 s0 = floor(b0)*2.0 + 1.0;
  vec4 s1 = floor(b1)*2.0 + 1.0;
  vec4 sh = -step(h, vec4(0.0));

  vec4 a0 = b0.xzyw + s0.xzyw*sh.xxyy ;
  vec4 a1 = b1.xzyw + s1.xzyw*sh.zzww ;

  vec3 p0 = vec3(a0.xy,h.x);
  vec3 p1 = vec3(a0.zw,h.y);
  vec3 p2 = vec3(a1.xy,h.z);
  vec3 p3 = vec3(a1.zw,h.w);

  //Normalise gradients
  vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
  p0 *= norm.x;
  p1 *= norm.y;
  p2 *= norm.z;
  p3 *= norm.w;

  // Mix final noise value
  vec4 m = max(0.6 - vec4(dot(x0,x0), dot(x1,x1), dot(x2,x2), dot(x3,x3)), 0.0);
  m = m * m;
  return 42.0 * dot( m*m, vec4( dot(p0,x0), dot(p1,x1), 
                                dot(p2,x2), dot(p3,x3) ) );
  }
`

function setupSplatmap(mesh) {
  /**
   * TODO
   * - vertex colors should shade terrain
   * - use alpha for a 4th texture layer
   *
   * NOTES
   * - blender gltf export doesnt support a complex triplanar splatmap shader
   *   so we force the splat and rgba textures into random material slots and
   *   reconstruct them here.
   */
  const original = mesh.material
  if (original.specularIntensityMap) original.specularIntensityMap.colorSpace = THREE.SRGBColorSpace
  if (original.transmissionMap) original.transmissionMap.colorSpace = THREE.SRGBColorSpace
  if (original.emissiveMap) original.emissiveMap.colorSpace = THREE.SRGBColorSpace
  if (original.normalMap) original.normalMap.colorSpace = THREE.SRGBColorSpace
  const uniforms = {
    splatTex: { value: original.map },
    rTex: { value: original.specularIntensityMap },
    gTex: { value: original.emissiveMap },
    bTex: { value: original.normalMap },
    aTex: { value: original.transmissionMap },
    rScale: { value: mesh.userData.red_scale || 1 },
    gScale: { value: mesh.userData.green_scale || 1 },
    bScale: { value: mesh.userData.blue_scale || 1 },
    aScale: { value: mesh.userData.alpha_scale || 1 },
  }
  // if (mesh.geometry.hasAttribute('_color')) {
  //   terrain.geometry.setAttribute('color', terrain.geometry.attributes._color)
  //   terrain.geometry.deleteAttribute('_color')
  //   hasVertexColors = true
  // }
  mesh.material = new CustomShaderMaterial({
    baseMaterial: THREE.MeshStandardMaterial,
    roughness: 1,
    metalness: 0,
    // vertexColors: true,
    uniforms,
    vertexShader: `
      varying vec2 vUv;
      varying vec3 vNorm;
      varying vec3 vPos;
      void main() {
        vUv = uv;
        vNorm = normalize(normal);
        vPos = position;
      }
    `,
    fragmentShader: `
      uniform sampler2D splatTex;
      uniform sampler2D rTex;
      uniform sampler2D gTex;
      uniform sampler2D bTex;
      uniform sampler2D aTex;
      uniform float rScale;
      uniform float gScale;
      uniform float bScale;
      uniform float aScale;
      varying vec2 vUv;
      varying vec3 vNorm;
      varying vec3 vPos;

      vec4 textureTriplanar(sampler2D tex, float scale, vec3 normal, vec3 position) {
          vec2 uv_x = position.yz * scale;
          vec2 uv_y = position.xz * scale;
          vec2 uv_z = position.xy * scale;
          vec4 xProjection = texture2D(tex, uv_x);
          vec4 yProjection = texture2D(tex, uv_y);
          vec4 zProjection = texture2D(tex, uv_z);
          vec3 weight = abs(normal);
          weight = pow(weight, vec3(4.0)); // bias towards the major axis
          weight = weight / (weight.x + weight.y + weight.z);
          return xProjection * weight.x + yProjection * weight.y + zProjection * weight.z;
      }

      vec3 tri(sampler2D t, float s) {
        return textureTriplanar(t, s, vNorm, vPos).rgb;
      }

      void main() {
          vec4 splat = texture2D(splatTex, vUv);
          vec4 result = vec4(0, 0, 0, 1.0);
          result += splat.r * textureTriplanar(rTex, rScale, vNorm, vPos);
          result += splat.g * textureTriplanar(gTex, gScale, vNorm, vPos);
          result += splat.b * textureTriplanar(bTex, bScale, vNorm, vPos);
          // result += splat.a * textureTriplanar(aTex, aScale, vNorm, vPos);
          // result += (1.0 - splat.a) * textureTriplanar(aTex, aScale, vNorm, vPos);
          // result *= vColor;
          csm_DiffuseColor *= result;
      }
    `,
  })
}

// ---------------------------------------------------------------------------
// Un-Finity — terrain « exp_terrain » (2026-09-30, « B + aube »)
// Carte de mélange RGB (roche, sable, sous-bois ; herbe = reste) + 5 couches PBR
// (albédo sRGB + NH : normale GL en RG, hauteur en B) chargées comme assets Hyperfy.
// Anti-répétition (décalage par cellules de bruit + 2e échelle au loin), variation
// de teinte à grande échelle, transitions bruitées et mélange par hauteur,
// sable mouillé au bord de l'eau, roche en projection triplanaire.
// ---------------------------------------------------------------------------
const terrainTexCache = new Map()
function loadTerrainTex(world, file, srgb) {
  if (terrainTexCache.has(file)) return terrainTexCache.get(file)
  const url = world.resolveURL('asset://' + file)
  const tex = new THREE.TextureLoader().load(url)
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping
  tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace
  tex.anisotropy = THREE.Texture.DEFAULT_ANISOTROPY || 1
  terrainTexCache.set(file, tex)
  return tex
}

function setupTerrain(mesh, world) {
  const cfg = mesh.userData.terrain || {}
  const original = mesh.material
  const splat = original.map
  if (splat) {
    splat.colorSpace = THREE.NoColorSpace
    splat.needsUpdate = true
  }
  const alb = cfg.alb.map(f => loadTerrainTex(world, f, true))
  const nh = cfg.nh.map(f => loadTerrainTex(world, f, false))
  const sc = cfg.taille_m.map(m => 1 / m)
  const uniforms = {
    splatTex: { value: splat },
    alb0: { value: alb[0] }, alb1: { value: alb[1] }, alb2: { value: alb[2] }, alb3: { value: alb[3] }, alb4: { value: alb[4] },
    nh0: { value: nh[0] }, nh2: { value: nh[2] }, nh3: { value: nh[3] }, nh4: { value: nh[4] },
    sc: { value: sc },
    rg: { value: cfg.rugosite },
    seaY: { value: cfg.seaY },
  }
  mesh.material = new CustomShaderMaterial({
    baseMaterial: THREE.MeshStandardMaterial,
    roughness: 1,
    metalness: 0,
    uniforms,
    vertexShader: `
      varying vec2 vTUv;
      varying vec3 vTPos;
      varying vec3 vTNw;
      varying float vTDist;
      void main() {
        vTUv = uv;
        vTPos = position;
        vTNw = normalize(mat3(modelMatrix) * normal);
        vec4 wp = modelMatrix * vec4(position, 1.0);
        vTDist = distance(wp.xyz, cameraPosition);
      }
    `,
    fragmentShader: `
      uniform sampler2D splatTex;
      uniform sampler2D alb0; uniform sampler2D alb1; uniform sampler2D alb2; uniform sampler2D alb3; uniform sampler2D alb4;
      uniform sampler2D nh0; uniform sampler2D nh2; uniform sampler2D nh3; uniform sampler2D nh4;
      uniform float sc[5];
      uniform float rg[5];
      uniform float seaY;
      varying vec2 vTUv;
      varying vec3 vTPos;
      varying vec3 vTNw;
      varying float vTDist;

      float tH21(vec2 p) { p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
      float tNoise(vec2 p) {
        vec2 i = floor(p); vec2 f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(tH21(i), tH21(i + vec2(1.0, 0.0)), u.x), mix(tH21(i + vec2(0.0, 1.0)), tH21(i + vec2(1.0, 1.0)), u.x), u.y);
      }
      float tFbm(vec2 p) { float a = 0.5; float s = 0.0; for (int i = 0; i < 4; i++) { s += a * tNoise(p); p = p * 2.03 + 17.1; a *= 0.5; } return s; }

      // anti-répétition : deux lectures décalées par cellules de bruit (méthode I. Quilez), + 2e échelle au loin
      void tSample(sampler2D ta, sampler2D tn, vec2 uv, float k, float far, out vec3 a, out vec3 n) {
        float l = k * 8.0; float i = floor(l); float f = fract(l);
        vec2 oa = sin(vec2(3.0, 7.0) * i); vec2 ob = sin(vec2(3.0, 7.0) * (i + 1.0));
        vec2 dx = dFdx(uv); vec2 dy = dFdy(uv);
        vec3 a1 = textureGrad(ta, uv + oa, dx, dy).rgb; vec3 a2 = textureGrad(ta, uv + ob, dx, dy).rgb;
        vec3 n1 = textureGrad(tn, uv + oa, dx, dy).rgb; vec3 n2 = textureGrad(tn, uv + ob, dx, dy).rgb;
        float t = smoothstep(0.2, 0.8, f - 0.1 * dot(a1 - a2, vec3(1.0)));
        a = mix(a1, a2, t); n = mix(n1, n2, t);
        if (far > 0.001) {
          vec2 uf = uv * 0.21 + 0.37;
          vec3 af = texture(ta, uf).rgb;
          a = mix(a, af, far * 0.7);
          n.xy = mix(n.xy, vec2(0.5), far * 0.5);
        }
      }
      // normale de carte (projection verticale) -> monde, mélange « whiteout »
      vec3 tTopN(vec3 nh, vec3 N, float str) {
        vec2 t = (nh.xy * 2.0 - 1.0) * str;
        float tz = sqrt(max(0.0, 1.0 - dot(t, t)));
        return normalize(vec3(t.x + N.x, tz * N.y, t.y + N.z));
      }

      void main() {
        vec3 N = normalize(vTNw);
        vec2 p = vTPos.xz;
        vec3 s = texture(splatTex, vTUv).rgb;
        float far = smoothstep(15.0, 80.0, vTDist);
        float k = tNoise(p * 0.045);

        // --- poids de base + transitions irrégulières (le bruit n'agit que dans les zones de transition)
        float wR = s.r; float wS = s.g; float wB = s.b;
        float nA = tFbm(p * 0.11); float nB = tFbm(p * 0.07 + 31.7);
        wS = clamp(wS + (nA - 0.5) * 1.6 * wS * (1.0 - wS) * 4.0 * 0.35, 0.0, 1.0);
        wB = clamp(wB + (nB - 0.5) * 1.6 * wB * (1.0 - wB) * 4.0 * 0.35, 0.0, 1.0);
        wR = clamp(wR + (nA - nB) * 0.6 * wR * (1.0 - wR) * 4.0, 0.0, 1.0);
        float tot = wR + wS + wB;
        if (tot > 1.0) { wR /= tot; wS /= tot; wB /= tot; tot = 1.0; }
        float wG = 1.0 - tot;
        // prairie tachetée : plaques de litière / terre dans l'herbe
        float patchN = smoothstep(0.56, 0.74, tFbm(p * 0.035 + 5.0));
        float dB = wG * patchN * 0.75; wG -= dB; wB += dB;
        // roche forcée sur les pentes fortes (sécurité si la carte est grossière)
        float slope = 1.0 - N.y;
        float rk = smoothstep(0.30, 0.45, slope);
        wR = max(wR, rk); float keep = 1.0 - rk; wS *= keep; wB *= keep; wG *= keep;

        // --- sable mouillé : bande irrégulière au-dessus de la mer + tout ce qui est sous l'eau
        float wetTop = seaY + 0.9 + 0.9 * tFbm(p * 0.05 + 7.3);
        float wet = 1.0 - smoothstep(seaY + 0.05, wetTop, vTPos.y);
        float wSd = wS * (1.0 - wet); float wSw = wS * wet;

        // --- lectures (on saute les couches absentes)
        vec3 aS = vec3(0.0), nS = vec3(0.5, 0.5, 0.5), aW = vec3(0.0), aG = vec3(0.0), nG = vec3(0.5), aB = vec3(0.0), nB3 = vec3(0.5), aR = vec3(0.0), nR = vec3(0.0, 1.0, 0.0);
        float hS = 0.0, hG = 0.0, hB = 0.0, hR = 0.0;
        if (wS > 0.002) { tSample(alb0, nh0, p * sc[0], k, far, aS, nS); hS = nS.b; }
        if (wSw > 0.002) { vec2 dxw = dFdx(p * sc[1]); vec2 dyw = dFdy(p * sc[1]); aW = textureGrad(alb1, p * sc[1] + sin(vec2(3.0, 7.0) * floor(k * 8.0)), dxw, dyw).rgb; }
        if (wG > 0.002) { tSample(alb2, nh2, p * sc[2], k, far, aG, nG); hG = nG.b; }
        if (wB > 0.002) { tSample(alb3, nh3, p * sc[3], fract(k + 0.37), far, aB, nB3); hB = nB3.b; }
        vec3 nRw = N;
        if (wR > 0.002) {
          vec3 bw = pow(abs(N), vec3(4.0)); bw /= (bw.x + bw.y + bw.z);
          vec3 q = vTPos * sc[4];
          vec3 ax = texture(alb4, q.zy).rgb, ay = texture(alb4, q.xz).rgb, az = texture(alb4, q.xy).rgb;
          vec3 nx = texture(nh4, q.zy).rgb, ny = texture(nh4, q.xz).rgb, nz = texture(nh4, q.xy).rgb;
          aR = ax * bw.x + ay * bw.y + az * bw.z;
          hR = nx.b * bw.x + ny.b * bw.y + nz.b * bw.z;
          vec2 tx = nx.xy * 2.0 - 1.0, ty = ny.xy * 2.0 - 1.0, tz = nz.xy * 2.0 - 1.0;
          vec3 wx = vec3(sqrt(max(0.0, 1.0 - dot(tx, tx))) * N.x, tx.y + N.y, tx.x + N.z);
          vec3 wy = vec3(ty.x + N.x, sqrt(max(0.0, 1.0 - dot(ty, ty))) * N.y, ty.y + N.z);
          vec3 wz = vec3(tz.x + N.x, tz.y + N.y, sqrt(max(0.0, 1.0 - dot(tz, tz))) * N.z);
          nRw = normalize(wx * bw.x + wy * bw.y + wz * bw.z);
        }

        // --- mélange par hauteur (transitions naturelles : l'herbe entre dans les creux du sable, etc.)
        float d = 0.18;
        float mS = hS + wSd, mW = hS * 0.6 + wSw, mG = hG + wG, mB = hB + wB, mR = hR + wR;
        float mx = max(max(max(mS, mW), max(mG, mB)), mR);
        float bS = wSd > 0.002 ? max(mS - mx + d, 0.0) : 0.0;
        float bW = wSw > 0.002 ? max(mW - mx + d, 0.0) : 0.0;
        float bG = wG > 0.002 ? max(mG - mx + d, 0.0) : 0.0;
        float bB = wB > 0.002 ? max(mB - mx + d, 0.0) : 0.0;
        float bR = wR > 0.002 ? max(mR - mx + d, 0.0) : 0.0;
        float bt = bS + bW + bG + bB + bR + 1e-5;
        bS /= bt; bW /= bt; bG /= bt; bB /= bt; bR /= bt;

        // --- variation de teinte à grande échelle
        float m1 = tFbm(p * 0.012 + 3.1); float m2 = tFbm(p * 0.021 + 11.7);
        aG *= mix(vec3(1.08, 1.02, 0.78), vec3(0.82, 0.98, 0.92), m2) * mix(0.86, 1.1, m1);
        aS *= vec3(1.10, 0.98, 0.80) * mix(vec3(1.04, 1.0, 0.94), vec3(0.96, 0.97, 0.99), m2) * mix(0.84, 0.98, m1);
        aB *= mix(0.85, 1.1, m1);
        aR *= mix(0.9, 1.08, m2);
        aW *= vec3(0.80, 0.76, 0.70);

        vec3 col = aS * bS + aW * bW + aG * bG + aB * bB + aR * bR;
        // sous l'eau : plus sombre et teinté
        float under = 1.0 - smoothstep(seaY - 0.6, seaY + 0.05, vTPos.y);
        col = mix(col, col * vec3(0.55, 0.72, 0.72), under);

        vec3 nW = normalize(
          tTopN(nS, N, 1.0) * bS + tTopN(nS, N, 0.35) * bW + tTopN(nG, N, 1.0) * bG + tTopN(nB3, N, 1.0) * bB + nRw * bR + N * 1e-4);
        float r = rg[0] * bS + rg[1] * bW + rg[2] * bG + rg[3] * bB + rg[4] * bR;
        // cavités un peu plus rugueuses
        r = clamp(r + (0.5 - (hS * bS + hG * bG + hB * bB + hR * bR)) * 0.1, 0.05, 1.0);

        csm_DiffuseColor = vec4(col, 1.0);
        csm_Roughness = r;
        csm_FragNormal = normalize((viewMatrix * vec4(nW, 0.0)).xyz);
      }
    `,
  })
}
